import { useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Mail, ArrowLeft } from 'lucide-react'
import { useAuth, EmailOtpUnavailableError } from '@/hooks/useAuth'
import { APP_NAME } from '@/config/constants'
import LoadingSpinner from '@/components/common/LoadingSpinner'

type Step = 'INPUT_EMAIL' | 'INPUT_CODE'

// Google サインインは外部リダイレクトで往復するため SessionStorage に保存して
// CallbackPage が return_to を引き継げるようにする。OTP の場合は同じセッション内なので
// 状態を取り出して即遷移するだけで済む。
const RETURN_TO_STORAGE_KEY = 'portal:returnTo'

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#FFC107"
      d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917"
    />
    <path
      fill="#FF3D00"
      d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44"
    />
    <path
      fill="#1976D2"
      d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917"
    />
  </svg>
)

// サブアプリからのリダイレクトに含まれる ?return_to= を安全に取り出す。
// 受け入れるのは ALLOWED_RETURN_DOMAIN (例: app.hakuou.co.jp) のサブドメインのみ。
// 不正な値は無視してデフォルトのポータルトップに戻す。
function getSafeReturnTo(): string | null {
  if (typeof window === 'undefined') return null
  const raw = new URLSearchParams(window.location.search).get('return_to')
  if (!raw) return null
  try {
    const url = new URL(raw)
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null
    const allowed = import.meta.env.VITE_ALLOWED_RETURN_DOMAIN
    if (!allowed) return null
    if (url.hostname === allowed || url.hostname.endsWith(`.${allowed}`)) {
      return url.toString()
    }
    return null
  } catch {
    return null
  }
}

export default function SignInPage() {
  const { t } = useTranslation()
  const { signInWithGoogle, requestEmailOtp, confirmEmailOtp } = useAuth()

  const [step, setStep] = useState<Step>('INPUT_EMAIL')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // マウント時に ?return_to= があれば SessionStorage に退避する。
  // Google サインインは外部リダイレクトで URL が失われるため、
  // CallbackPage 側で SessionStorage を読んで遷移する。
  useEffect(() => {
    const returnTo = getSafeReturnTo()
    if (returnTo) sessionStorage.setItem(RETURN_TO_STORAGE_KEY, returnTo)
  }, [])

  function redirectAfterSignIn() {
    const returnTo = sessionStorage.getItem(RETURN_TO_STORAGE_KEY)
    if (returnTo) {
      sessionStorage.removeItem(RETURN_TO_STORAGE_KEY)
      window.location.href = returnTo
    }
  }

  async function handleRequestCode(e: FormEvent) {
    e.preventDefault()
    if (!email) return
    setBusy(true)
    setError(null)
    try {
      const next = await requestEmailOtp(email)
      if (next === 'DONE') return
      setStep('INPUT_CODE')
    } catch (err) {
      if (err instanceof EmailOtpUnavailableError) {
        setError(t('signIn.errors.otpUnavailable'))
      } else {
        setError(extractErrorMessage(err, t('signIn.errors.requestFailed')))
      }
    } finally {
      setBusy(false)
    }
  }

  async function handleConfirm(e: FormEvent) {
    e.preventDefault()
    if (!code) return
    setBusy(true)
    setError(null)
    try {
      await confirmEmailOtp(code)
      redirectAfterSignIn()
    } catch (err) {
      setError(extractErrorMessage(err, t('signIn.errors.invalidCode')))
    } finally {
      setBusy(false)
    }
  }

  async function handleGoogle() {
    setBusy(true)
    setError(null)
    try {
      await signInWithGoogle()
    } catch (err) {
      setBusy(false)
      setError(extractErrorMessage(err, t('signIn.errors.googleFailed')))
    }
  }

  function backToEmail() {
    setStep('INPUT_EMAIL')
    setCode('')
    setError(null)
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 px-4">
      <div className="w-full max-w-sm bg-white rounded-xl shadow-md p-8">
        <div className="flex flex-col items-center mb-6">
          <img src="/logo.png" alt={APP_NAME} className="h-10 w-auto mb-2" />
          <h1 className="text-lg font-semibold text-gray-800">{t('signIn.title')}</h1>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {step === 'INPUT_EMAIL' && (
          <>
            <button
              type="button"
              onClick={handleGoogle}
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 border border-gray-300 rounded-lg py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              <GoogleIcon />
              {t('signIn.continueWithGoogle')}
            </button>

            <div className="my-5 flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400">{t('signIn.or')}</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            <form onSubmit={handleRequestCode} className="space-y-3">
              <label className="block">
                <span className="text-sm text-gray-700">{t('signIn.email')}</span>
                <input
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </label>
              <button
                type="submit"
                disabled={busy || !email}
                className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {busy ? <LoadingSpinner size="sm" /> : <Mail className="w-4 h-4" />}
                {t('signIn.sendCode')}
              </button>
            </form>
          </>
        )}

        {step === 'INPUT_CODE' && (
          <>
            <p className="text-sm text-gray-600 mb-2">
              {t('signIn.codeSentTo', { email })}
            </p>
            <p className="text-xs text-gray-500 mb-4">
              {t('signIn.codeNotArriving')}
            </p>
            <form onSubmit={handleConfirm} className="space-y-3">
              <label className="block">
                <span className="text-sm text-gray-700">{t('signIn.code')}</span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm tracking-widest text-center focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </label>
              <button
                type="submit"
                disabled={busy || !code}
                className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {busy ? <LoadingSpinner size="sm" /> : null}
                {t('signIn.verify')}
              </button>
              <button
                type="button"
                onClick={backToEmail}
                className="w-full flex items-center justify-center gap-1 text-sm text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                {t('signIn.backToEmail')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}

function extractErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const msg = (err as { message?: unknown }).message
    if (typeof msg === 'string' && msg.length > 0) return msg
  }
  return fallback
}
